import { useState } from "react";

const checklist = [
  { item: "Warm neutral tone", note: "Ivory, oat, sand, or soft taupe — avoid cool grey" },
  { item: "Natural fibre material", note: "Wool, jute, cotton, or a wool-cotton blend" },
  { item: "Correct size", note: "Front legs of all seating should sit on the rug" },
  { item: "Appropriate pile height", note: "Low to medium pile for living rooms" },
  { item: "Subtle texture or quiet pattern", note: "Let texture do the work, not colour" },
  { item: "Rug pad underneath", note: "Prevents slipping and extends the rug\'s life" },
  { item: "Consistent undertone with the room", note: "Warm rug in a warm room, always" },
];

const faqs = [
  {
    q: "What size rug do I need for a living room?",
    a: "As a general rule, go larger than you think. In a typical living room, a 200x300cm rug is a good starting point. The front legs of your sofa and chairs should sit on the rug — this visually ties the seating area together and makes the room feel more considered. A rug that is too small makes the furniture look like it is floating.",
  },
  {
    q: "What is the best material for a neutral rug?",
    a: "Wool is the gold standard — it is durable, naturally stain-resistant, and has a warmth and depth that synthetic fibres cannot replicate. For a more relaxed, earthy look, jute and sisal flatweaves are excellent. Cotton is a good budget option. Avoid polypropylene if you want the rug to feel genuinely warm and natural — it tends to look flat in person.",
  },
  {
    q: "How do I keep a neutral rug looking clean?",
    a: "Vacuum regularly with a low-suction setting to avoid pulling the fibres. For wool rugs, avoid over-wetting — spot clean with a damp cloth and mild detergent. A rug pad underneath helps prevent dirt from working its way into the fibres. For high-traffic areas, consider a professional clean once a year to maintain the texture and tone.",
  },
  {
    q: "Can I layer rugs in a neutral room?",
    a: "Yes — layering rugs is one of the most effective ways to add depth and warmth to a neutral room. A common approach is to place a smaller flatweave or vintage rug over a larger jute or sisal base. Keep the tones within the same warm family and let the textures contrast rather than the colours.",
  },
];

export default function NRFormula() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-0 pb-16">

      <div className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>The Formula</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          The Neutral Rug Checklist
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-3">
          {checklist.map((c, i) => (
            <div key={i} className="flex items-start gap-4 border-b border-[#EDE6DA] pb-3 last:border-0">
              <div className="w-5 h-5 flex items-center justify-center rounded-full border border-[#C8BAA8] flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-[#C8BAA8] text-[10px]" />
              </div>
              <div>
                <p className="text-[#2C2825] text-sm" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{c.item}</p>
                <p className="text-[#B5A898] text-xs mt-0.5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{c.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>FAQ</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Frequently Asked Questions
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-[#EDE6DA] rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#F5F1E8]/50 transition-colors duration-200"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="text-[#2C2825] text-sm leading-snug pr-4" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{faq.q}</span>
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`ri-add-line text-[#8B8378] text-base transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5">
                  <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <p className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          A neutral rug is one of the quietest decisions you can make — and one of the most lasting.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          When you choose the right tone, material, and size, a neutral rug becomes the foundation that makes everything else in the room feel more intentional.
          It grounds the furniture, softens the space, and adds a layer of warmth that no other element can quite replicate.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Take your time choosing. A good rug only gets better with age.
        </p>
      </div>

    </div>
  );
}
